'use client';

import { useSyncExternalStore } from 'react';

/**
 * Edge that holds the system's vertical bar column, or null when the system
 * bars are horizontal.
 *
 * On iPhone Duo (iOS 27.1 SDK builds) iOS moves the status bar, tab bars and
 * toolbars into a vertical column on one side in most poses. WKWebView only
 * exposes that through the safe-area insets: the column side reports a wide
 * inset (~84pt) while the opposite side stays near zero. A classic iPhone in
 * landscape reports equal left and right insets, so asymmetry is the signal.
 */
export type SystemBarRail = 'left' | 'right' | null;

const RAIL_MIN_INSET = 60;
const FLAT_EDGE_MAX_INSET = 20;

/** Capacitor's System Bars plugin injects --safe-area-inset-* where WebView env() is unreliable. */
export const SAFE_AREA_INSET = {
  top: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
  right: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
  bottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
  left: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
} as const;

let probe: HTMLDivElement | null = null;

function getProbe() {
  if (probe?.isConnected) return probe;

  probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  Object.assign(probe.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    paddingLeft: SAFE_AREA_INSET.left,
    paddingRight: SAFE_AREA_INSET.right,
  });
  document.body.appendChild(probe);

  return probe;
}

function getSnapshot(): SystemBarRail {
  const style = getComputedStyle(getProbe());
  const left = parseFloat(style.paddingLeft) || 0;
  const right = parseFloat(style.paddingRight) || 0;

  if (right >= RAIL_MIN_INSET && left <= FLAT_EDGE_MAX_INSET) return 'right';
  if (left >= RAIL_MIN_INSET && right <= FLAT_EDGE_MAX_INSET) return 'left';

  return null;
}

function getServerSnapshot(): SystemBarRail {
  return null;
}

function subscribe(onChange: () => void) {
  // Insets settle a frame after the resize that a fold, unfold or rotation fires.
  const onResize = () => {
    onChange();
    requestAnimationFrame(onChange);
  };

  window.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onResize);
  screen.orientation?.addEventListener('change', onResize);

  return () => {
    window.removeEventListener('resize', onResize);
    window.visualViewport?.removeEventListener('resize', onResize);
    screen.orientation?.removeEventListener('change', onResize);
  };
}

export function useSystemBarRail() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
